import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAudio } from '../audio/AudioProvider'
import { renderMarkdown } from '../lib/markdown'
import { activeLine, highlight, lineMatches, parseTranscript, speakerSlots } from '../lib/transcript'
import type { Theory } from '../lib/types'
import { plural } from '../lib/format'
import { Icon } from './Icon'

/** Enough to show who is in the room and how it reads, without a wall of text. */
const PREVIEW_LINES = 5

/** Lines further than this from the active one all sit at the dimmest step. */
const DIM_RANGE = 5

/**
 * A transcript that follows the recording.
 *
 * Falls back to plain rendering when the text has no cues in it — a transcript
 * typed by hand has nothing to sync against, and a highlight that never moves
 * is worse than no highlight.
 */
export function Transcript({ theory }: { theory: Theory }) {
  const audio = useAudio()
  const [expanded, setExpanded] = useState(false)
  const [following, setFollowing] = useState(true)
  const [query, setQuery] = useState('')
  const bodyRef = useRef<HTMLDivElement>(null)

  const lines = useMemo(() => parseTranscript(theory.transcript), [theory.transcript])
  const slots = useMemo(() => speakerSlots(lines), [lines])
  const cued = useMemo(() => lines.filter((line) => line.at !== null).length, [lines])

  const isCurrent = audio.isCurrent(theory.slug)
  const active = isCurrent ? activeLine(lines, audio.currentTime) : -1

  const searching = query.trim().length > 0

  // Rows carry their original index so filtering never breaks the link between
  // a row and the recording — the active line and the scroll target are both
  // addressed by position in the full transcript, not in what is on screen.
  const rows = useMemo(
    () =>
      lines
        .map((line, index) => ({ line, index }))
        .filter(({ line }) => lineMatches(line, query)),
    [lines, query],
  )

  // A transcript short enough to fit is just shown, and gets no search box —
  // both the preview fade and a "find in transcript" field would be furniture
  // around content you can already read in one glance.
  const long = lines.length > PREVIEW_LINES

  /**
   * Once this recording is the one loaded, the transcript stops being a wall you
   * expand and becomes a fixed-height window that scrolls itself.
   *
   * The cap is the whole point. Expanded, a real episode is several thousand
   * pixels tall, so "keep the current line in view" can only be honoured by
   * scrolling the document — which drags the player, the header and everything
   * else past the reader for the entire recording. Bounded, the page holds still
   * and only the window moves.
   */
  const stage = isCurrent && long && !searching && !expanded
  // Every line is rendered unless this is the closed preview. Searching counts
  // as open: filtering down to four hits and then hiding three of them behind a
  // fade would be a strange thing to do to someone.
  const open = expanded || searching || stage || !long
  const visible = open ? rows : rows.slice(0, PREVIEW_LINES)

  /**
   * Any deliberate scroll hands control back to the reader. Listening for wheel
   * and touch rather than scroll matters: the auto-scroll below fires scroll
   * events itself, so watching those would switch this off on the first move.
   */
  useEffect(() => {
    if (!open) return
    // In the window it is the window that scrolls, so that is what has to be
    // listened to — a wheel event inside it never reaches the document.
    const target: HTMLElement | Window = (stage && bodyRef.current) || window
    const release = () => setFollowing(false)
    target.addEventListener('wheel', release, { passive: true })
    target.addEventListener('touchmove', release, { passive: true })
    return () => {
      target.removeEventListener('wheel', release)
      target.removeEventListener('touchmove', release)
    }
  }, [open, stage])

  // Keep the playing line in view. Skipped under reduced-motion, where a page
  // that scrolls itself is precisely what the setting is asking you not to do.
  useEffect(() => {
    if (!open || !following || active < 0 || !audio.playing) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const box = bodyRef.current
    const el = box?.querySelector<HTMLElement>(`[data-line="${active}"]`)
    if (!box || !el) return

    if (stage) {
      // Centre the line by moving the window's own scrollTop. scrollIntoView is
      // wrong here however it is configured: it walks up the tree and scrolls
      // every scrollable ancestor it finds, the document included, which is the
      // exact behaviour the window exists to stop.
      /**
       * A turn taller than the pane is pinned by its top, not centred.
       *
       * Centring is right for a one-line remark. Max holds the floor for a
       * paragraph at a time, and centring a turn taller than the window puts
       * its first half above the top edge — the reader arrives in the middle of
       * a sentence with the beginning already gone. Pinned, they get the start
       * of what is being said and the rest scrolls into view underneath.
       */
      const tall = el.offsetHeight > box.clientHeight * 0.55
      box.scrollTo({
        top: tall
          ? el.offsetTop - box.clientHeight * 0.06
          : el.offsetTop - box.clientHeight * 0.38 + el.offsetHeight / 2,
        behavior: 'smooth',
      })
    } else {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }, [active, open, following, stage, audio.playing])

  /**
   * Scrolling the playing line back into view re-arms following on its own.
   *
   * Requiring the button for this is a poor trade: having scrolled back to
   * exactly where the recording is, being made to click something to say so is
   * busywork. The `left` flag is what keeps it predictable — the line has to
   * leave the pane first, so nudging the wheel a notch while the line is still
   * on screen releases follow and does not immediately re-arm it.
   */
  const left = useRef(false)
  // Cleared when following is given up, and only then. Clearing it inside the
  // effect below would wipe it every time the active line advanced — a second
  // later the pane would have forgotten the line was ever scrolled away from,
  // and re-arming could never trigger.
  useEffect(() => {
    if (!following) left.current = false
  }, [following])

  useEffect(() => {
    const box = bodyRef.current
    if (!stage || following || !box) return
    const onScroll = () => {
      const el = box.querySelector<HTMLElement>(`[data-line="${active}"]`)
      if (!el) return
      const b = box.getBoundingClientRect()
      const l = el.getBoundingClientRect()
      const inside = l.top >= b.top && l.bottom <= b.bottom
      if (!inside) left.current = true
      else if (left.current) setFollowing(true)
    }
    box.addEventListener('scroll', onScroll, { passive: true })
    return () => box.removeEventListener('scroll', onScroll)
  }, [stage, following, active])

  /** Opening from a click on a line should land on that line, not the top. */
  const expand = useCallback(() => {
    setExpanded(true)
    setFollowing(true)
  }, [])

  const seekTo = useCallback(
    (at: number) => {
      audio.play(theory, [], at)
      setFollowing(true)
    },
    [audio, theory],
  )

  /** Timestamps written inside a line are still markdown links, not elements. */
  const onBodyClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const link = (event.target as HTMLElement).closest('a')
    const at = Number(link?.getAttribute('href')?.match(/^#t=(\d+)$/)?.[1])
    if (!link || !Number.isFinite(at)) return
    event.preventDefault()
    seekTo(at)
  }

  if (!theory.transcript.trim()) return null

  // Nothing to follow along with — render it as the article it is.
  if (cued < 2) {
    return (
      <section className="transcript" aria-label="Transcript">
        <header className="transcript-head">
          <Icon name="quote" size={15} />
          <span>Transcript</span>
        </header>
        <div
          className="prose transcript-body is-open"
          onClick={onBodyClick}
          dangerouslySetInnerHTML={{ __html: renderMarkdown(theory.transcript, { timestamps: true }) }}
        />
      </section>
    )
  }

  return (
    <section className="transcript" aria-label="Transcript">
      <header className="transcript-head">
        <Icon name="quote" size={15} />
        <span>Transcript</span>

        {long && (
        <div className="transcript-find">
          <Icon name="search" size={14} />
          <input
            type="search"
            value={query}
            placeholder="Find in transcript"
            aria-label="Find in transcript"
            spellCheck={false}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => event.key === 'Escape' && setQuery('')}
          />
          {searching && (
            <button type="button" className="transcript-find-clear" onClick={() => setQuery('')}>
              Clear
            </button>
          )}
        </div>
        )}

        <span className="mono transcript-hint">
          {searching
            ? `${rows.length} of ${plural(lines.length, 'line')}`
            : `${plural(lines.length, 'line')} · word for word`}
        </span>
      </header>

      <div className="transcript-scroller">
      <div
        ref={bodyRef}
        className={`transcript-body ${open ? 'is-open' : 'is-preview'}${stage ? ' is-stage' : ''}`}
        onClick={onBodyClick}
      >
        {visible.map(({ line, index }) => {
          // Max talks in paragraphs, so his name can land eight times in a row.
          // Printing it once per turn is how a script reads, and it takes a
          // column of repeated labels out of the reader's way. Off while
          // filtering, where neighbouring rows are not neighbours in the audio
          // and "same as above" would be a lie.
          const runOn =
            !searching && index > 0 && !!line.speaker && lines[index - 1]?.speaker === line.speaker
          return (
          <p
            key={index}
            data-line={index}
            className={`tline${index === active ? ' is-active' : ''}${runOn ? ' is-runon' : ''}`}
            style={{
              ...(slots.has(line.speaker ?? '')
                ? { '--slot': `var(--speaker-${slots.get(line.speaker ?? '')})` }
                : {}),
              // How far this line is from the one being spoken, which is what
              // the fade off either side of the active line is computed from.
              // Capped so a line 200 away is not asked for a negative opacity.
              ...(stage && active >= 0
                ? { '--dist': Math.min(DIM_RANGE, Math.abs(index - active)) }
                : {}),
            } as React.CSSProperties}
          >
            {line.at !== null && (
              <button
                type="button"
                className="tline-at mono"
                onClick={() => seekTo(line.at as number)}
                aria-label={`Play from ${formatCue(line.at)}`}
              >
                {formatCue(line.at)}
              </button>
            )}
            {line.speaker && !runOn && (
              <span className="tline-who">
                <span className="tline-dot" aria-hidden="true" />
                {line.speaker}
              </span>
            )}
            <span
              className="tline-text"
              dangerouslySetInnerHTML={{ __html: searching ? highlight(line.html, query) : line.html }}
            />
          </p>
          )
        })}

        {searching && rows.length === 0 && (
          <p className="transcript-empty">
            Nothing in this recording matches “{query.trim()}”.
          </p>
        )}
      </div>

      {/* Sits on the pane rather than down by the player. It is the control for
          this box, so it belongs against this box — floated to the bottom of the
          screen it reads as part of the player and gets missed. */}
      {open && !following && isCurrent && audio.playing && active >= 0 && (
        <button
          type="button"
          className={`transcript-follow${stage ? ' is-onpane' : ''}`}
          onClick={() => setFollowing(true)}
        >
          <span className="transcript-pulse" aria-hidden="true" />
          Follow along
        </button>
      )}
      </div>

      <footer className="transcript-foot">
        {/* The way out of the window, for reading rather than listening. Offered
            from the preview and from the window alike — the window is bounded on
            purpose, so there has to be something that unbounds it. */}
        {searching || !long ? null : expanded ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setExpanded(false)}>
            Collapse
          </button>
        ) : (
          <button type="button" className="btn btn-ghost btn-sm transcript-more" onClick={expand}>
            Read all {plural(lines.length, 'line')}
          </button>
        )}

      </footer>
    </section>
  )
}

function formatCue(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}
